import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import useStyles from './styles'
import { ClinicAndFlags } from '@/modules/Auth/auth-types'
import { useHistory, useParams, useLocation } from 'react-router'
import { Location } from 'history'
import service from '../../auth-services'
import themeServices from '@/theme/theme.services'
import rebrandedThemeServices from '@/rebrandedTheme/theme.services'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { User } from '@/modules/Auth/classes/User'
import LoadingPage from '@/components/LoadingPage'
import redirectToOldApplication from '@/utils/redirectToOldApplication'
import { setRefreshToken, setToken } from '@/utils/token'
import CustomLayout from '@/components/CustomLayout'
import { useModalContext } from '@/components/RebrandedModal/contexts/ModalContext'
import { TermsAndConditionsModal } from '../../components/TermsAndConditionsModal'
import UserProfileCardDetails from '../../components/UserProfileCardDetails'
import UserProfileCard from '../../components/UserProfileCard'
import { SpecialtiesModal } from '../../components/Modal/SpecialtiesModal'
import { getCache, setCache } from '@/utils/cache'
import { RenderIf } from '@/components/RenderIf'
import { useRebrandingThemeGeneratorContext } from '@/rebrandedTheme/contexts/ThemeGeneratorContext'
import { HeroWrapper } from '@/styles/rebranded/HeroWrapper'
import { ConexaLogo, Text, toast } from '@conexasaude/conexa-hero'
import { openWhatsappSupport } from '@/utils/openWhatsappSupport'

type ChoiceLocation = {
  from: Location
}
type ChoiceParams = {
  clinic?: string
}

const Choice: FunctionComponent = () => {
  const params = useParams<ChoiceParams>()
  const location = useLocation<ChoiceLocation>()
  const history = useHistory()
  const styles = useStyles()
  const { users, choiceUser, setUserClinic } = useAuthContext()
  const { setThemeColors } = useThemeGeneratorContext()
  const { setThemeColors: setRebrandingThemeColors } =
    useRebrandingThemeGeneratorContext()
  const { setSpecialities } = useMedicalSpecialitiesContext()
  const { showModal } = useModalContext()
  const [loading, setLoading] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [currentProfileId, setCurrentProfileId] = useState(0)
  const [termsModalActive, setTermsModalActive] = useState(false)
  const [imageError, setImageError] = useState(false)

  const disableNewHub = useMemo(() => {
    if (users) {
      return users.some(user => user.disableHub)
    }
  }, [users])

  const redirectAfterChoice = useCallback(
    (pathname?: string) => {
      if (location?.state?.from) {
        history.push(location?.state?.from?.pathname)
        return
      }

      history.push(pathname)
    },
    [history, location?.state?.from],
  )

  const setIfHaveOneUser = useCallback(async () => {
    try {
      setCurrentUser(users[0])
      const token = users[0].token
      const refreshToken = users[0].refreshToken
      setToken(token)
      setRefreshToken(refreshToken)
      setCache('user', users[0].id)

      const termsChecked = await service.checkTermsAcceptance()
      setLoading(true)
      const userClinic: ClinicAndFlags = await service.getClinic(users[0].token)
      const clinicColors = await themeServices.getClinicColorsById(
        userClinic.clinic.idClinica,
      )
      const clinicColorsRebranding =
        await rebrandedThemeServices.getClinicColorsById(
          userClinic.clinic.idClinica,
        )

      const ip = await service.getUserIP().catch(() => {
        console.error('Erro ao pegar ip')
        return ''
      })
      if (!users[0]?.flagAccessedBefore)
        await service.validateFirstAccess(users[0]?.token)
      choiceUser(users[0].id, ip)

      if (!termsChecked) {
        setTermsModalActive(true)
        showModal('TERMS_AND_CONDITIONS', {
          title: 'Termos e Condições Gerais de Uso',
          persistent: true,
        })
        return
      }
      setThemeColors(clinicColors)
      setRebrandingThemeColors(clinicColorsRebranding)
      setUserClinic(userClinic)
      setSpecialities(userClinic.clinic)

      if (userClinic?.flags?.HabilitarAutenticacaoDoisFatoresLoginPaciente) {
        let twoFactorAuthRoute = '/autenticacao-dois-fatores'

        const uniqueCode = getCache<string>('unique-code')

        if (uniqueCode) {
          twoFactorAuthRoute += `/${uniqueCode}`
        }

        history.push(twoFactorAuthRoute, location?.state?.from)
        return
      }

      redirectAfterChoice('/')
    } catch (e) {
      toast({
        message: 'Algo de errado ocorreu. Tente novamente',
        variant: 'error',
      })
    }
  }, [
    users,
    choiceUser,
    setThemeColors,
    setRebrandingThemeColors,
    setUserClinic,
    setSpecialities,
    redirectAfterChoice,
    showModal,
    history,
    location?.state?.from,
  ])

  const setUser = useCallback(
    async (user: User) => {
      setTermsModalActive(false)
      try {
        setLoading(true)
        setCurrentProfileId(user.id)
        const userClinic: ClinicAndFlags = await service.getClinic(user.token)
        const clinicColors = await themeServices.getClinicColorsById(
          userClinic.clinic.idClinica,
        )
        const clinicColorsRebranding =
          await rebrandedThemeServices.getClinicColorsById(
            userClinic.clinic.idClinica,
          )
        const ip = await service.getUserIP().catch(() => {
          console.error('Erro ao pegar ip')
          return ''
        })
        if (!user?.flagAccessedBefore)
          await service.validateFirstAccess(user?.token)
        choiceUser(user.id, ip)
        setThemeColors(clinicColors)
        setRebrandingThemeColors(clinicColorsRebranding)
        setUserClinic(userClinic)
        setSpecialities(userClinic.clinic)

        if (userClinic?.flags?.PermiteGeolocalizacao) {
          navigator.geolocation.getCurrentPosition(position => {
            const coords = position.coords
            if (coords) {
              service.sendGeolocation({
                accuracy: coords.accuracy,
                altitude: coords.altitude,
                altitudeAccuracy: coords.altitudeAccuracy,
                heading: coords.heading,
                latitude: coords.latitude,
                longitude: coords.longitude,
                speed: coords.speed,
              })
            }
          })
        }

        if (userClinic?.flags?.HabilitarAutenticacaoDoisFatoresLoginPaciente) {
          let twoFactorAuthRoute = '/autenticacao-dois-fatores'

          const uniqueCode = getCache<string>('unique-code')

          if (uniqueCode) {
            twoFactorAuthRoute += `/${uniqueCode}`
          }

          history.push(twoFactorAuthRoute, location?.state?.from)
          return
        }

        redirectAfterChoice('/')
      } catch (e) {
        console.log(e)
        setLoading(false)
        setCurrentProfileId(0)
        toast({
          message: 'Algo de errado ocorreu. Tente novamente',
          variant: 'error',
        })
      }
    },
    [
      choiceUser,
      setThemeColors,
      setRebrandingThemeColors,
      setUserClinic,
      setSpecialities,
      redirectAfterChoice,
      history,
      location?.state,
    ],
  )

  const handleUserSelect = useCallback(async (user: User) => {
    try {
      setLoading(true)
      setCurrentProfileId(user.id)
      setCurrentUser(user)
      setToken(user.token)
      setRefreshToken(user.refreshToken)
      setCache('user', user.id)

      await service.validateToken(user.token)

      const termsChecked = await service.checkTermsAcceptance()
      if (!termsChecked) {
        setTermsModalActive(true)
        showModal('TERMS_AND_CONDITIONS', {
          title: 'Termos e Condições Gerais de Uso',
          persistent: true,
        })
        setLoading(false)
        setCurrentProfileId(0)
        return
      }
      if (!user.isAllowedToNewApplication) {
        setLoading(true)
        setCurrentProfileId(user.id)
        setToken(user.token)
        redirectToOldApplication()
        setLoading(false)
        setCurrentProfileId(0)
        return
      }
    } catch (err) {
      setLoading(false)
      setCurrentProfileId(0)
      const errorMessage =
        err?.response?.data?.msg ||
        err?.response?.data?.object?.msg ||
        err.message
      toast({
        message: errorMessage,
        variant: 'error',
      })
    }
    setUser(user)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!users) {
      history.push('/login', {
        from: location?.state?.from,
      })
    }
    if (!users.length) {
      setTimeout(() => {
        history.push('/login', {
          from: location?.state?.from,
        })
        toast({
          message:
            'Não foi possível continuar. Entre em contato com a sua operadora de saúde ou empresa.',
          variant: 'error',
        })
      }, 2000)
    } else if (users?.length === 1) {
      if (!users[0]?.isAllowedToNewApplication) {
        redirectToOldApplication(users[0]?.token)
        return
      }

      setIfHaveOneUser()
    }

    // window?.Appcues?.anonymous()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const onShowModalSpeciality = useCallback(
    (user: User) => {
      setCurrentUser(user)
      setTimeout(() => {
        showModal('SPECIALTIES_MODAL', {
          title: 'Especialidades',
        })
      }, 1)
    },
    [showModal],
  )

  return (
    <HeroWrapper>
      <CustomLayout>
        <div css={styles.pageContainer}>
          <div css={styles.headerChoice}>
            <div>
              <RenderIf condition={!!params.clinic && !imageError}>
                <img
                  src={`${process.env.REACT_APP_LINK_LOGO_AWS}${params.clinic}.png`}
                  alt="logo"
                  css={styles.logoWhiteLabel}
                  onError={event => {
                    event.currentTarget.onerror = null
                    setImageError(true)
                    event.currentTarget.style.height = 'auto'
                  }}
                />
              </RenderIf>
              <RenderIf condition={!params.clinic || imageError}>
                <ConexaLogo base="colorful" inverse width={125} height={48} />
              </RenderIf>
            </div>
          </div>
          <div css={styles.container}>
            {users?.length > 1 && (
              <div css={styles.titleLayout}>
                <div>
                  <Text variant="headingLarge" css={styles.titleChoice}>
                    O que deseja acessar?
                  </Text>
                  <Text variant="subheading" css={styles.subtitleChoice}>
                    Escolha a clínica, hospital ou convênio.
                  </Text>
                </div>
              </div>
            )}
            {users?.length > 1 ? (
              users.map(user => {
                if (!disableNewHub) {
                  return (
                    <UserProfileCardDetails
                      className="MultiPerfil-SeleçãoPerfil-Clique_perfil"
                      photo={user.photo}
                      name={user.socialNameOrName}
                      busy={loading && currentProfileId === user.id}
                      clinic={user.clinic.name}
                      onClick={() => handleUserSelect(user)}
                      key={user.id}
                      specialities={user.clinicSpecialites}
                      type={user.holder}
                      token={user.token}
                    />
                  )
                } else {
                  return (
                    <UserProfileCard
                      className="MultiPerfil-SeleçãoPerfil-Clique_perfil"
                      photo={user.photo}
                      name={user.socialNameOrName}
                      busy={loading && currentProfileId === user.id}
                      clinic={user.clinic.name}
                      onClick={() => handleUserSelect(user)}
                      key={user.id}
                      type={user.holder}
                      token={user.token}
                      onActionClick={() => onShowModalSpeciality(user)}
                    />
                  )
                }
              })
            ) : (
              <div css={styles.loadingPageWrapper}>
                {termsModalActive && <LoadingPage withoutNav={true} />}
              </div>
            )}
            <Text
              renderAs="p"
              variant="linkUnderline"
              css={styles.linkSupportChoice}
              onClick={openWhatsappSupport}
            >
              Precisa de ajuda?
            </Text>
          </div>
          <TermsAndConditionsModal
            redirectTo={''}
            directLogin={() => setUser(currentUser)}
            setLoading={setLoading}
            userToken={currentUser?.token}
          />
          <SpecialtiesModal
            photo={currentUser?.photo}
            name={currentUser?.socialNameOrName}
            clinic={currentUser?.clinic.name}
            key={currentUser?.id}
            specialities={currentUser?.clinicSpecialites}
            type={currentUser?.holder}
          />
        </div>
      </CustomLayout>
    </HeroWrapper>
  )
}

export default Choice
